import { Box, Flex, Text, Divider } from '@chakra-ui/react';
import { Button, TextArea, useToast } from '@morressier/alembic';
import {
  IRecommendation,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import { FormProvider, useForm } from 'react-hook-form';
import { useModerateCOCRecommendation } from 'src/api/commands';
import { FieldLabel } from 'src/components/Field';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { getFileUploadArrayFFDConfigs } from 'src/utils/formHelpers';
import RecommendationFormFieldMultipleFileUpload from './RecommendationFormFieldMultipleFileUpload';

import {
  checkFileUpload,
  FileItem
} from 'src/pages/Organization/public/Journal';

export type IModerateRecommendationForm = {
  commentForAuthor: string;
  moderatedCommentForAuthor: string;
  attachments: ISubmissionFile[];
  moderatedAttachments: ISubmissionFile[];
  decision: string;
  commentForEditor: string;
};

type Props = {
  onClose: () => void;
  Recommendation: IRecommendation;
  Role: string;
  UserId: string;
};
export const ModerateEICComment: React.FC<Props> = ({
  onClose,
  Recommendation,
  Role
}) => {
  const { organizationId, workflowResponse } = useSubmissionData();


  const originalAttachments = Recommendation?.attachments ?? [];
  const existingModerated = Recommendation?.moderatedAttachments ?? [];


  const moderatedRefs = new Set(
    existingModerated.map(a => a?.referenceName).filter(Boolean)
  );

  const mergedModeratedAttachments = [
    ...existingModerated,
    ...originalAttachments.filter(
      a => a?.referenceName && !moderatedRefs.has(a.referenceName)
    )
  ];

  const methods = useForm<IModerateRecommendationForm>({
    defaultValues: {
      commentForAuthor: Recommendation?.commentForAuthor,
      moderatedCommentForAuthor:
        Recommendation?.moderatedCommentForAuthor ||
        Recommendation?.commentForAuthor,
      attachments: Recommendation?.attachments,
      moderatedAttachments: mergedModeratedAttachments,
      decision: Recommendation?.decision,
      commentForEditor: Recommendation?.commentForEditor
    }
  });
  const checkIsFileLoading = () => {
    const values = methods.getValues();
    const status = checkFileUpload(values?.moderatedAttachments as FileItem);
    return status;
  };

  const isFileUploading = checkIsFileLoading();

  const { submissionId, submissionContextId } = workflowResponse;
  const { register, watch, getValues } = methods;
  const toast = useToast();
  const submitCOCRecommendationMutation = useModerateCOCRecommendation({
    submissionContextId,
    submissionId,
    organizationId,
    recommendationId: Recommendation?.id || ''
  });
  const onSubmitRecommendation = async (data: IModerateRecommendationForm) => {
    const res = await submitCOCRecommendationMutation.mutateAsync(data);

    if (res.isSuccess) {
      onClose();
      return toast({
        variant: 'success',
        title: `${Role} comment moderated`,
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message?.error ? res.message?.error : res?.message,
      isClosable: true
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <Box maxW="900px" mx="auto" mt={4} padding={6} borderColor="gray.200">
          <Box pb={4}>
            <Text textStyle={'headingGroup'}>Moderate {Role}</Text>
            <Text textStyle={'textContext'}>
              You are editing a user comment. Please review the text and
              attachments below. You may upload additional files if necessary.
            </Text>
          </Box>
          <Divider mb={2}></Divider>
          {/* Edit Comment */}
          <Flex flexDirection={'column'} gap={4}>
            <Box>
              <FieldLabel
                label="Edit Comment"
                subText={''}
                isRequired={false}
              />
              <TextArea
                value={watch('moderatedCommentForAuthor')}
                {...register('moderatedCommentForAuthor')}
                minHeight={100}
                maxHeight={600}
                mt={2}
                placeholder="Write a comment for author(s)"
              />
            </Box>

            {/* Attach Files */}
            <Box>
              <RecommendationFormFieldMultipleFileUpload
                config={getFileUploadArrayFFDConfigs('moderatedAttachments')}
                submissionId={submissionId}
                submissionContextId={submissionContextId}
                Recommendation={Recommendation}
              />
            </Box>

            {/* Footer */}
            <Flex justify="flex-end" gap={3} py={4}>
              <Button
                variant="secondary"
                disabled={isFileUploading}
                onClick={onClose}
              >
                Exit without saving
              </Button>
              <Button
                onClick={() => {
                  onSubmitRecommendation(getValues());
                }}
                disabled={isFileUploading}
              >
                Submit changes
              </Button>
            </Flex>
          </Flex>
        </Box>
      </FormProvider>
    </>
  );
};
